import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Address } from 'viem';
import { useChainId } from 'wagmi';
import { formatUnits } from 'viem';
import { getPools, type Pool } from '@/onchain/pools';
import { assetBySymbol, TEQUITY_SYMBOLS, type AssetConfig } from '@/onchain/assets';
import { getUTCHour } from '@/app/api/utils/truncateTimestampToInterval';
import logger from '@/utils/logger';
import { TIME_PERIODS_SECONDS } from '@/utils/timeConstants';
import {
  calculate24hVolumeUSD,
  calculateVolumeUSDFromDayData,
  calculate24hPriceChange,
  calculateVolumeChangePercent,
} from '@/features/tokenized-equity/utils/calculations';

export type TokenizedEquityData = {
  id: string;
  name: string;
  symbol: string;
  imageUrl: string;
  /** Token contract address */
  tokenAddress: Address;
  stockPriceUsd: number;
  stockPriceChange: number;
  avgTokenPriceUsd: number;
  tokenMarketCapUsd: number;
  tokenVolumeUsd: number;
  tokenVolumeChange: number;
};

export type TokenizedEquitiesSummary = {
  marketCap: number;
  marketCapChange: number;
  tradeVolume: number;
  tradeVolumeChange: number;
  topPerformer: {
    symbol: string;
    imageUrl: string;
    priceUsd: number;
    change24h: number;
  };
  topLoser: {
    symbol: string;
    imageUrl: string;
    priceUsd: number;
    change24h: number;
  };
};

// TODO: Implement stock price API integration to fetch real stock prices.
// This is a placeholder value - real stock prices should come from a financial data provider
// (e.g., Alpha Vantage, IEX Cloud, Yahoo Finance API) to show accurate underlying stock prices.
const DEFAULT_STOCK_PRICE_USD = 10;

// Response types for the subgraph query
type SubgraphTokenData = {
  id: string;
  symbol: string;
  decimals: string;
  totalSupply: string;
  name: string;
};

type SubgraphPoolDayData = {
  date: number;
  token0Price: string;
  token1Price: string;
  volumeToken0: string;
  volumeToken1: string;
  volumeUSD: string;
  feesToken0: string;
  feesToken1: string;
};

type SubgraphPoolHourData = {
  periodStartUnix: number;
  token0Price: string;
  token1Price: string;
  volumeToken0: string;
  volumeToken1: string;
  volumeUSD: string;
  feesToken0: string;
  feesToken1: string;
};

type SubgraphPoolData = {
  id: string;
  token0: SubgraphTokenData;
  token1: SubgraphTokenData;
  token0Price: string;
  token1Price: string;
  totalValueLockedToken0: string;
  totalValueLockedToken1: string;
  poolDayData: SubgraphPoolDayData[];
  poolHourData: SubgraphPoolHourData[];
};

/**
 * Retrieves tEquity pools for a given chain from the pool configuration.
 * @param chainId - The blockchain chain ID to filter pools by
 * @returns Array of pools that contain at least one tEquity token
 */
function getTEquityPools(chainId: number): Pool[] {
  const allPools = getPools(chainId) ?? [];
  return allPools.filter((pool) => pool.tokens.some((token) => TEQUITY_SYMBOLS.includes(token)));
}

/**
 * Extracts tEquity token information from a pool.
 * @param pool - The pool to extract token info from
 * @param chainId - The blockchain chain ID for address lookup
 * @returns Token symbol, address, and asset config, or null if no tEquity token found
 */
function getTEquityTokenFromPool(
  pool: Pool,
  chainId: number,
): { symbol: string; address: `0x${string}`; asset: AssetConfig } | null {
  const tEquitySymbol = pool.tokens.find((token) => TEQUITY_SYMBOLS.includes(token));
  if (!tEquitySymbol) return null;

  const asset = assetBySymbol(tEquitySymbol);
  if (!asset) return null;

  const address = asset.address[chainId] as `0x${string}`;
  return { symbol: tEquitySymbol, address, asset };
}

export function useTokenizedEquitiesData() {
  const chainId = useChainId();

  // Get tEquity pools for the current chain
  const tEquityPools = useMemo(() => {
    const pools = getTEquityPools(chainId);
    logger.debug('[TokenizedEquities] tEquity pools found', { chainId, poolCount: pools.length });
    return pools;
  }, [chainId]);

  // Get tEquity token info from pools
  const tEquityTokens = useMemo(() => {
    return tEquityPools
      .map((pool) => getTEquityTokenFromPool(pool, chainId))
      .filter((token): token is NonNullable<typeof token> => token !== null);
  }, [tEquityPools, chainId]);

  // Get token addresses for API query (lowercase for subgraph compatibility)
  const tokenAddresses = useMemo(
    () => tEquityTokens.map((token) => token.address.toLowerCase()),
    [tEquityTokens],
  );

  // Fetch all data via API route (server-side subgraph call with caching)
  // Include hour timestamp in query key to allow cache sharing across users for the same hour
  const hourTimestamp = getUTCHour(Math.floor(Date.now() / 1000));
  const { data: subgraphData, isLoading } = useQuery({
    queryKey: ['tokenizedEquitiesData', chainId, tokenAddresses, hourTimestamp],
    queryFn: async () => {
      const response = await fetch(
        `/api/v1/tokenizedEquities?chainId=${chainId}&tokenAddresses=${tokenAddresses.join(',')}`,
      );

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const result = (await response.json()) as { ok: boolean; pools: SubgraphPoolData[] };
      return result.pools ?? [];
    },
    enabled: tokenAddresses.length > 0,
    staleTime: 60000, // 1 minute
    gcTime: 120000, // 2 minutes
  });

  // Build equities data from subgraph response
  const equities = useMemo<TokenizedEquityData[]>(() => {
    if (!subgraphData?.length || !tEquityTokens.length) return [];

    const twentyFourHoursAgo =
      Math.floor(Date.now() / 1000) - TIME_PERIODS_SECONDS.TWENTY_FOUR_HOURS;

    return tEquityTokens
      .map((token) => {
        // Find the pool that contains this token
        const poolData = subgraphData.find(
          (p) =>
            p.token0.id.toLowerCase() === token.address.toLowerCase() ||
            p.token1.id.toLowerCase() === token.address.toLowerCase(),
        );

        if (!poolData) {
          logger.warn('[TokenizedEquities] No pool data found', { symbol: token.symbol });
          return null;
        }

        // Determine if our tEquity token is token0 or token1
        const isToken0 = poolData.token0.id.toLowerCase() === token.address.toLowerCase();
        const tEquityToken = isToken0 ? poolData.token0 : poolData.token1;

        // Get metadata dynamically from subgraph and asset config
        // Priority: subgraph name > asset config name, asset config imageUrl
        const name = tEquityToken.name || token.asset.name;
        const imageUrl = token.asset.imageUrl;

        // Get token price (price in terms of the other token, which should be a stablecoin)
        // token0Price = price of token0 in terms of token1
        // token1Price = price of token1 in terms of token0
        const priceStr = isToken0 ? poolData.token0Price : poolData.token1Price;
        const tokenPrice = parseFloat(priceStr) || 0;

        // Calculate market cap: price * total supply (using BigInt for precision)
        const decimals = parseInt(tEquityToken.decimals) || 18;
        const totalSupplyBigInt = BigInt(tEquityToken.totalSupply || '0');
        const totalSupply = parseFloat(formatUnits(totalSupplyBigInt, decimals));
        const tokenMarketCapUsd = totalSupply * tokenPrice;

        // Calculate USD volume - use volumeUSD directly from subgraph (already in USD)
        const hourlyData = poolData.poolHourData ?? [];
        const dailyData = poolData.poolDayData ?? [];

        let volume24hUsd = 0;
        let volume24hPreviousUsd = 0;

        if (hourlyData.length > 0) {
          // Use hourly volumeUSD for precise 24h calculation
          const hourlyVolumes = calculate24hVolumeUSD(hourlyData, twentyFourHoursAgo);
          volume24hUsd = hourlyVolumes.current;
          volume24hPreviousUsd = hourlyVolumes.previous;
        } else if (dailyData.length > 0) {
          // Fallback to daily volumeUSD
          const dailyVolumes = calculateVolumeUSDFromDayData(dailyData);
          volume24hUsd = dailyVolumes.latest;
          volume24hPreviousUsd = dailyVolumes.previous;
        }

        const tokenVolumeUsd = volume24hUsd;

        // Calculate volume change percentage
        const tokenVolumeChange = calculateVolumeChangePercent(volume24hUsd, volume24hPreviousUsd);

        // Calculate actual 24h price change from hourly price data
        const priceKey = isToken0 ? 'token0Price' : 'token1Price';
        const priceChange = calculate24hPriceChange(hourlyData, priceKey, twentyFourHoursAgo);
        const stockPriceChange = priceChange.changePercent;

        return {
          id: poolData.id,
          name,
          symbol: token.symbol,
          imageUrl,
          tokenAddress: token.address,
          stockPriceUsd: DEFAULT_STOCK_PRICE_USD,
          stockPriceChange,
          avgTokenPriceUsd: tokenPrice,
          tokenMarketCapUsd,
          tokenVolumeUsd,
          tokenVolumeChange,
        };
      })
      .filter((equity): equity is TokenizedEquityData => equity !== null);
  }, [subgraphData, tEquityTokens]);

  // Build summary from equities data
  const summary = useMemo<TokenizedEquitiesSummary>(() => {
    if (!equities.length) {
      return {
        marketCap: 0,
        marketCapChange: 0,
        tradeVolume: 0,
        tradeVolumeChange: 0,
        topPerformer: {
          symbol: '-',
          imageUrl: '',
          priceUsd: 0,
          change24h: 0,
        },
        topLoser: {
          symbol: '-',
          imageUrl: '',
          priceUsd: 0,
          change24h: 0,
        },
      };
    }

    // Calculate total market cap and volume
    const totalMarketCap = equities.reduce((sum, e) => sum + e.tokenMarketCapUsd, 0);
    const totalVolume = equities.reduce((sum, e) => sum + e.tokenVolumeUsd, 0);

    // Sort by price change to find top performer and loser
    const sortedByChange = [...equities].sort((a, b) => b.stockPriceChange - a.stockPriceChange);
    const topPerformer = sortedByChange[0];
    const topLoser = sortedByChange[sortedByChange.length - 1];

    // Calculate overall market cap change (average of price changes)
    const avgMarketCapChange =
      equities.length > 0
        ? equities.reduce((sum, e) => sum + e.stockPriceChange, 0) / equities.length
        : 0;

    // Calculate overall volume change (average of volume changes)
    const avgVolumeChange =
      equities.length > 0
        ? equities.reduce((sum, e) => sum + e.tokenVolumeChange, 0) / equities.length
        : 0;

    return {
      marketCap: totalMarketCap,
      marketCapChange: Math.round(avgMarketCapChange * 100) / 100,
      tradeVolume: totalVolume,
      tradeVolumeChange: Math.round(avgVolumeChange * 100) / 100,
      topPerformer: {
        symbol: topPerformer?.symbol ?? '-',
        imageUrl: topPerformer?.imageUrl ?? '',
        priceUsd: topPerformer?.avgTokenPriceUsd ?? 0,
        change24h: topPerformer?.stockPriceChange ?? 0,
      },
      topLoser: {
        symbol: topLoser?.symbol ?? '-',
        imageUrl: topLoser?.imageUrl ?? '',
        priceUsd: topLoser?.avgTokenPriceUsd ?? 0,
        change24h: topLoser?.stockPriceChange ?? 0,
      },
    };
  }, [equities]);

  return {
    equities,
    summary,
    isLoading,
  };
}
